// features/articles/articlesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../app/firebase';
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  where,
  limit,
  deleteField,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';

import {
  deriveLegacyHtmlFromCards,
  isArticleCardNonempty,
  normalizeCardsArray,
} from '../../utils/articleHelpers';
import {
  deleteArticleStorageFiles,
  isValidArticleImageUrl,
} from '../../utils/uploadArticleImage';
import {
  buildSiteContentWrite,
  siteContentShouldPersist,
  sitePublishError,
} from '../../utils/siteArticle';

// ——— helpers ———
const slugKey = (lang, slug) => `${lang}:${slug}`;

const tsToMillis = (ts) => {
  if (ts == null) return null;
  if (typeof ts === 'number' && Number.isFinite(ts)) return ts;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return null;
};

const normalizeDictionaryDoc = (id, data = {}) => ({
  id,
  ...data,
  createdAt: tsToMillis(data.createdAt),
  updatedAt: tsToMillis(data.updatedAt),
});

const normalizeArticle = (id, data, extra = {}) => ({
  id,
  ...data,
  cards: normalizeCardsArray(data.cards),
  publishedAt: tsToMillis(data.publishedAt),
  createdAt: tsToMillis(data.createdAt),
  updatedAt: tsToMillis(data.updatedAt),
  ...extra,
});

// ——— thunks ———

// список статей (общий для всех языков; локализацию выбираем на уровне UI)
export const fetchArticles = createAsyncThunk('articles/fetchAll', async () => {
  const q = query(collection(db, 'articles'), orderBy('publishedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => normalizeArticle(d.id, d.data()));
});

export const fetchArticleById = createAsyncThunk('articles/fetchById', async (id) => {
  const ref = doc(db, 'articles', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Article not found');
  return normalizeArticle(snap.id, snap.data());
});

export const fetchArticleBySlug = createAsyncThunk('articles/fetchBySlug', async ({ slug, lang }) => {
  const q = query(collection(db, 'articles'), where(`slug.${lang}`, '==', slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) throw new Error('Article not found');
  const d = snap.docs[0];
  return normalizeArticle(d.id, d.data(), { _slugLang: { lang, slug } });
});

export const fetchArticleCategories = createAsyncThunk(
  'articles/fetchArticleCategories',
  async (_, { rejectWithValue }) => {
    try {
      const snap = await getDocs(collection(db, 'articleCategories'));
      const list = snap.docs.map((d) =>
        normalizeDictionaryDoc(d.id, d.data() || {}),
      );
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return list;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to load categories');
    }
  },
);

export const fetchArticlePetTags = createAsyncThunk(
  'articles/fetchArticlePetTags',
  async (_, { rejectWithValue }) => {
    try {
      const snap = await getDocs(collection(db, 'articlePetTags'));
      const list = snap.docs.map((d) =>
        normalizeDictionaryDoc(d.id, d.data() || {}),
      );
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return list;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to load pet tags');
    }
  },
);

export const fetchReadArticleIds = createAsyncThunk(
  'articles/fetchReadArticleIds',
  async (_, { getState, rejectWithValue }) => {
    const uid = getState()?.users?.currentUser?.id;
    if (!uid) return [];
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (!snap.exists()) return [];
      const raw = snap.data()?.readArticles;
      if (!Array.isArray(raw)) return [];
      return raw.filter((v) => typeof v === 'string' && v.length > 0);
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to load readArticles');
    }
  },
);

export const markArticleRead = createAsyncThunk(
  'articles/markArticleRead',
  async (articleId, { getState, rejectWithValue }) => {
    const uid = getState()?.users?.currentUser?.id;
    if (!uid) return rejectWithValue('Not signed in');
    try {
      await updateDoc(doc(db, 'users', uid), {
        readArticles: arrayUnion(articleId),
      });
      return articleId;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to mark read');
    }
  },
);

export const markArticleUnread = createAsyncThunk(
  'articles/markArticleUnread',
  async (articleId, { getState, rejectWithValue }) => {
    const uid = getState()?.users?.currentUser?.id;
    if (!uid) return rejectWithValue('Not signed in');
    try {
      await updateDoc(doc(db, 'users', uid), {
        readArticles: arrayRemove(articleId),
      });
      return articleId;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to mark unread');
    }
  },
);

// создание/обновление статьи (для админа)
export const saveArticle = createAsyncThunk('articles/save', async ({ id, data }, { rejectWithValue }) => {
  try {
    const now = serverTimestamp();
    // Если publishedAt - это Date объект, конвертируем в Timestamp
    let publishedAt;
    if (data.publishedAt instanceof Date) {
      publishedAt = Timestamp.fromDate(data.publishedAt);
    } else if (data.publishedAt != null && data.publishedAt !== '') {
      publishedAt =
        typeof data.publishedAt === 'number'
          ? Timestamp.fromMillis(data.publishedAt)
          : data.publishedAt;
    } else {
      publishedAt = now;
    }

    const cardsNormalized = normalizeCardsArray(data.cards || []).filter(
      isArticleCardNonempty,
    );

    const hasCards = cardsNormalized.length > 0;

    const contentHtmlMerged = hasCards
      ? deriveLegacyHtmlFromCards(cardsNormalized)
      : { en: '', ru: '', sr: '' };

    const slug = typeof data.slug === 'object' && data.slug ? { ...data.slug } : {};

    const title =
      typeof data.title === 'object' && data.title ? { ...data.title } : {};
    const subtitle =
      typeof data.subtitle === 'object' && data.subtitle
        ? { ...data.subtitle }
        : {};

    const categoryIds = Array.isArray(data.categoryIds)
      ? data.categoryIds.filter((x) => typeof x === 'string' && x)
      : [];
    const petTagIds = Array.isArray(data.petTagIds)
      ? data.petTagIds.filter((x) => typeof x === 'string' && x)
      : [];

    const coverTrimmed =
      data.coverUrl && String(data.coverUrl).trim()
        ? String(data.coverUrl).trim()
        : "";
    if (coverTrimmed && !isValidArticleImageUrl(coverTrimmed)) {
      throw new Error(
        "coverUrl must be a full HTTPS URL (not a filename or data URL)",
      );
    }

    cardsNormalized.forEach((card, i) => {
      const img = card.imageUrl && String(card.imageUrl).trim();
      if (img && !isValidArticleImageUrl(img)) {
        throw new Error(
          `Card ${i + 1} imageUrl must be a full HTTPS URL`,
        );
      }
    });

    const webOnly = Boolean(data.webOnly);
    const mobileOnly = Boolean(data.mobileOnly);
    const showOnSite = Boolean(data.showOnSite);
    const pinned = Boolean(data.pinned);
    const siteInput = data.site && typeof data.site === 'object' ? data.site : null;
    const ogTrimmed = String(siteInput?.ogImageUrl || '').trim();
    const publishError = sitePublishError({
      showOnSite,
      slugEn: slug.en,
      site: siteInput,
      ogInvalid: Boolean(ogTrimmed) && !isValidArticleImageUrl(ogTrimmed),
    });
    if (publishError) throw new Error(publishError);

    const articleData = {
      slug,
      title,
      subtitle,
      contentHtml: contentHtmlMerged,
      coverUrl: coverTrimmed || null,
      publishedAt,
      updatedAt: now,
      ...(id ? {} : { createdAt: now }),
      cards: hasCards ? cardsNormalized : deleteField(),
      categoryIds: categoryIds.length ? categoryIds : deleteField(),
      petTagIds: petTagIds.length ? petTagIds : deleteField(),
      webOnly: webOnly ? true : deleteField(),
      mobileOnly: mobileOnly ? true : deleteField(),
      showOnSite: showOnSite ? true : deleteField(),
      pinned: pinned ? true : deleteField(),
    };

    const ref = id ? doc(db, 'articles', id) : doc(collection(db, 'articles'));
    await setDoc(ref, articleData, { merge: true });

    if (siteInput) {
      const siteWrite = buildSiteContentWrite(siteInput);
      if (siteContentShouldPersist(showOnSite, siteWrite)) {
        await setDoc(doc(db, 'articles', ref.id, 'web', 'content'), {
          ...siteWrite,
          updatedAt: serverTimestamp(),
        });
      }
    }
    
    // После сохранения получаем документ обратно для нормализации
    const savedSnap = await getDoc(ref);
    return normalizeArticle(ref.id, savedSnap.data());
  } catch (e) {
    return rejectWithValue(e.message || 'Failed to save article');
  }
});

export const deleteArticle = createAsyncThunk(
  'articles/delete',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error('Article id is required');
      const snap = await getDoc(doc(db, 'articles', id));
      if (snap.exists()) {
        const cards = normalizeCardsArray(snap.data()?.cards);
        const cardIds = cards
          .map((c) => c.id)
          .filter((cid) => typeof cid === 'string' && cid);
        await deleteArticleStorageFiles(id, cardIds);
      }
      await deleteDoc(doc(db, 'articles', id, 'web', 'content'));
      await deleteDoc(doc(db, 'articles', id));
      return id;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to delete article');
    }
  },
);

// ——— state ———

const initialState = {
  items: [],           // список (сырые документы)
  byId: {},            // кэш по id
  bySlugLang: {},      // кэш по ключу `${lang}:${slug}` -> статья
  articleCategories: [],
  articlePetTags: [],
  loadingDictionaries: false,
  dictionariesError: null,
  readArticleIds: [],
  readArticlesError: null,
  loadingList: false,
  loadingItem: false,
  saving: false,
  deleting: false,
  error: null,
};

// ——— slice ———

const articlesSlice = createSlice({
  name: 'articles',
  initialState,
  reducers: {
    resetArticlesState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchArticles
      .addCase(fetchArticles.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.loadingList = false;
        state.items = action.payload;
        action.payload.forEach(a => {
          state.byId[a.id] = a;
          // Прокинем быстрые кэши по известным слагам (если есть)
          if (a.slug && typeof a.slug === 'object') {
            Object.entries(a.slug).forEach(([lang, s]) => {
              if (s) state.bySlugLang[slugKey(lang, s)] = a;
            });
          }
        });
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.error?.message || 'Failed to load articles';
      })

      // fetchArticleById
      .addCase(fetchArticleById.pending, (state) => {
        state.loadingItem = true;
        state.error = null;
      })
      .addCase(fetchArticleById.fulfilled, (state, action) => {
        state.loadingItem = false;
        const a = action.payload;
        state.byId[a.id] = a;
        if (a.slug && typeof a.slug === 'object') {
          Object.entries(a.slug).forEach(([lang, s]) => {
            if (s) state.bySlugLang[slugKey(lang, s)] = a;
          });
        }
      })
      .addCase(fetchArticleById.rejected, (state, action) => {
        state.loadingItem = false;
        state.error = action.error?.message || 'Failed to load article';
      })

      // fetchArticleBySlug
      .addCase(fetchArticleBySlug.pending, (state) => {
        state.loadingItem = true;
        state.error = null;
      })
      .addCase(fetchArticleBySlug.fulfilled, (state, action) => {
        state.loadingItem = false;
        const a = action.payload;
        state.byId[a.id] = a;
        // первичный ключ, по которому запрашивали
        if (a._slugLang) {
          const { lang, slug } = a._slugLang;
          state.bySlugLang[slugKey(lang, slug)] = a;
        }
        // и заодно раскидаем все известные слаги из документа
        if (a.slug && typeof a.slug === 'object') {
          Object.entries(a.slug).forEach(([lang, s]) => {
            if (s) state.bySlugLang[slugKey(lang, s)] = a;
          });
        }
      })
      .addCase(fetchArticleBySlug.rejected, (state, action) => {
        state.loadingItem = false;
        state.error = action.error?.message || 'Failed to load article';
      })

      .addCase(fetchArticleCategories.pending, (state) => {
        state.loadingDictionaries = true;
        state.dictionariesError = null;
      })
      .addCase(fetchArticleCategories.fulfilled, (state, action) => {
        state.loadingDictionaries = false;
        state.articleCategories = action.payload;
      })
      .addCase(fetchArticleCategories.rejected, (state, action) => {
        state.loadingDictionaries = false;
        state.dictionariesError = action.payload || action.error.message;
      })

      .addCase(fetchArticlePetTags.fulfilled, (state, action) => {
        state.articlePetTags = action.payload;
      })

      .addCase(fetchReadArticleIds.fulfilled, (state, action) => {
        state.readArticleIds = action.payload;
        state.readArticlesError = null;
      })
      .addCase(fetchReadArticleIds.rejected, (state, action) => {
        state.readArticlesError =
          action.payload || action.error.message || 'readArticles failed';
      })

      .addCase(markArticleRead.fulfilled, (state, action) => {
        const aid = action.payload;
        if (aid && !state.readArticleIds.includes(aid))
          state.readArticleIds.push(aid);
      })
      .addCase(markArticleUnread.fulfilled, (state, action) => {
        const aid = action.payload;
        state.readArticleIds = state.readArticleIds.filter((x) => x !== aid);
      })

      // saveArticle
      .addCase(saveArticle.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveArticle.fulfilled, (state, action) => {
        state.saving = false;
        const a = action.payload;
        state.byId[a.id] = a;
        // Обновляем список, если статья уже там есть
        const index = state.items.findIndex(item => item.id === a.id);
        if (index >= 0) {
          state.items[index] = a;
        } else {
          // Если новая, добавляем в начало (так как сортировка по publishedAt desc)
          state.items.unshift(a);
          state.items.sort((a, b) => (b.publishedAt || 0) - (a.publishedAt || 0));
        }
        // Обновляем кэш по слагам
        if (a.slug && typeof a.slug === 'object') {
          Object.entries(a.slug).forEach(([lang, s]) => {
            if (s) state.bySlugLang[slugKey(lang, s)] = a;
          });
        }
      })
      .addCase(saveArticle.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || 'Failed to save article';
      })

      .addCase(deleteArticle.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.deleting = false;
        const id = action.payload;
        state.items = state.items.filter((a) => a.id !== id);
        delete state.byId[id];
        Object.keys(state.bySlugLang).forEach((key) => {
          if (state.bySlugLang[key]?.id === id) delete state.bySlugLang[key];
        });
        state.readArticleIds = state.readArticleIds.filter((x) => x !== id);
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.deleting = false;
        state.error =
          action.payload || action.error?.message || 'Failed to delete article';
      });
  },
});

// ——— selectors ———

export const { resetArticlesState } = articlesSlice.actions;

export const selectArticles = (state) => state.articles.items;
export const selectArticlesLoading = (state) => state.articles.loadingList;
export const selectArticleLoading = (state) => state.articles.loadingItem;
export const selectArticleSaving = (state) => state.articles.saving;
export const selectArticleDeleting = (state) => state.articles.deleting;
export const selectArticleError = (state) => state.articles.error;

export const selectArticleCategories = (state) => state.articles.articleCategories;

export const selectArticlePetTags = (state) => state.articles.articlePetTags;

export const selectReadArticleIds = (state) => state.articles.readArticleIds;

export const selectArticleById = (id) => (state) => state.articles.byId[id];
export const selectArticleBySlugLang = (slug, lang) => (state) =>
  state.articles.bySlugLang[slugKey(lang, slug)];

// Удобный хелпер, если нужен текущий язык (например, в компоненте вместе с i18n.language):
export const makeSelectArticleBySlugWithLang = (slug) => (state, lang) =>
  state.articles.bySlugLang[slugKey(lang, slug)];

export default articlesSlice.reducer;

